import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Storage key for token
const TOKEN_KEY = 'b2b_token';

// Create axios instance with defaults
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors (session expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Session expired - clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('b2b_user');
      // Redirect to login if not already there
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (identifier, password, loginType = 'normal') =>
    api.post('/auth/login', { identifier, password, login_type: loginType }),
  register: (userData) => api.post('/auth/register', userData),
  logout: (token) => api.post('/auth/logout', {}, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  validateSession: (token) => api.get('/auth/validate', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getCurrentUser: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: (status, role) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (role) params.append('role', role);
    return api.get(`/users?${params.toString()}`);
  },
  update: (userId, userData) => api.put(`/users/${userId}`, userData),
  delete: (userId) => api.delete(`/users/${userId}`),
  approve: (userId) => api.put(`/users/${userId}/approve`),
  reject: (userId) => api.put(`/users/${userId}/reject`),
  updateAdminSettings: (settings) => api.put('/admin/settings', settings),
};

// Products API
export const productsAPI = {
  getAll: () => api.get('/products'),
  create: (product) => api.post('/products', product),
  update: (productId, product) => api.put(`/products/${productId}`, product),
  delete: (productId) => api.delete(`/products/${productId}`),
  deleteAll: () => api.delete('/products'),
};

// Warehouses API
export const warehousesAPI = {
  getAll: () => api.get('/warehouses'),
  create: (name) => api.post('/warehouses', { name }),
  delete: (warehouseId) => api.delete(`/warehouses/${warehouseId}`),
};

// Orders API
export const ordersAPI = {
  getAll: (userId, userType, date) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (userType) params.append('user_type', userType);
    if (date) params.append('date', date);
    return api.get(`/orders?${params.toString()}`);
  },
  create: (order, userId, userName, userType) =>
    api.post(`/orders?user_id=${userId}&user_name=${encodeURIComponent(userName)}&user_type=${userType}`, order),
  updateStatus: (orderId, status) => api.put(`/orders/${orderId}/status?status=${status}`),
};

// Bank Accounts API
export const bankAccountsAPI = {
  getAll: () => api.get('/bank-accounts'),
  create: (account) => api.post('/bank-accounts', account),
  update: (accountId, account) => api.put(`/bank-accounts/${accountId}`, account),
  delete: (accountId) => api.delete(`/bank-accounts/${accountId}`),
};

// Online Payment API
export const onlinePaymentAPI = {
  get: () => api.get('/online-payment'),
  update: (settings) => api.put('/online-payment', settings),
};

// Credit Card Systems API
export const creditCardSystemsAPI = {
  getAll: () => api.get('/credit-card-systems'),
  create: (system) => api.post('/credit-card-systems', system),
  update: (systemId, system) => api.put(`/credit-card-systems/${systemId}`, system),
  delete: (systemId) => api.delete(`/credit-card-systems/${systemId}`),
};

// Discount Settings API
export const discountSettingsAPI = {
  get: () => api.get('/discount-settings'),
  update: (settings) => api.put('/discount-settings', settings),
};

// Account Balance API
export const accountBalanceAPI = {
  get: (userId) => api.get(`/account-balance/${userId}`),
  getTransactions: (userId) => api.get(`/transactions/${userId}`),
};

// Brands API
export const brandsAPI = {
  getAll: () => api.get('/brands'),
  create: (name, logo = '') => api.post(`/brands?name=${encodeURIComponent(name)}&logo=${encodeURIComponent(logo)}`),
  delete: (brandId) => api.delete(`/brands/${brandId}`),
};

// Quick Sales API
export const quickSalesAPI = {
  getAll: () => api.get('/quick-sales'),
  create: (sale) => api.post('/quick-sales', sale),
  delete: (saleId) => api.delete(`/quick-sales/${saleId}`),
};

// Admin Settings API (Kalıcı Ayarlar - Veritabanında)
export const adminSettingsAPI = {
  get: () => api.get('/admin-settings'),
  update: (settings) => api.put('/admin-settings', settings),
  getPublic: () => api.get('/public-settings'),
};

// Price List API (Genel Liste Fiyatları)
export const priceListAPI = {
  getAll: () => api.get('/price-list'),
  getAllToptanci: () => api.get('/price-list/toptanci'),
  upload: (items) => api.post('/price-list/upload', { items }),
  deleteAll: () => api.delete('/price-list'),
  getCount: () => api.get('/price-list/count'),
};

// Init API
export const initAPI = {
  init: () => api.post('/init'),
};

export default api;
